import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSetDatasetAutoSession, datasetKeys } from '../hooks/useDataset';
import { useSkyboxSessions } from '../hooks/useSkyboxSessions';
import { useLionAutoStatus, lionAutoKeys } from '../hooks/useLionAuto';
import { SkyboxSessionChip, SkyboxSessionPanel } from './SkyboxSessionPanel';
import { formatTimeEastern, parseUtcDate } from '../utils/dates';
import {
  runnerStatusInfo, isDryRun, modeBanner, errorText as runnerErrorText, loginPaceChip, runnerStatusReadError,
} from '../utils/lionAutoRunner';
import { fmtInt, text, errorMessage, statusSessions, sessionName, autoIds, sameIdSet } from '../utils/datasetStatus';

// Admin → Data: Lion Auto's Skybox logins (purpose 'auto'). Tick the inactive
// users whose Skybox session the unattended runner may use, then Save the
// list: the runner works on one login per run (a login without a recent 429
// first, else the one with the most room; each has its own Skybox budget) and
// skips a login Skybox refused while the others carry on. The server's rules
// decide (USER_ACTIVE, NO_SKYBOX_SESSION, SLOT_IN_USE, at most 5) and answer
// why not; a blocked row says why before you try (and no box can be ticked
// past the cap). A save that failed part-way answers what is saved now and
// the status is re-read (useDataset). Each saved login's row
// shows its session, calls in the last 10 s, its 429 / pace, why the runner
// set it aside, and its own paste / renew panel (the Admin → Users one).

const CHIP_CLASS = { ok: 'tm-chip-good', warn: 'tm-chip-warn', bad: 'tm-chip-bad' };
// The server's cap (datasetRules.MAX_AUTO_LOGINS): more boxes can't be ticked
const MAX_AUTO_LOGINS = 5;

// Why a login can't be one of Lion Auto's (null = it can): the server's own rules
function blockedWhy(s) {
  if (s.userActive === true) return 'active user — not allowed';
  if (s.purpose === 'backfill') return 'backfill login';
  if (s.status === 'none') return 'no Skybox session yet';
  return null;
}

// What the Lion Auto runner says right now (its own status; no Skybox call):
// Idle, No login, Login(s) down, …, and the mode; or that it could not be read
function AutoRunnerLine({ query }) {
  if (query.error?.response?.status === 404) return <div className="ai-admin-note" style={{ marginTop: 6 }}>The Lion Auto runner is not on this server yet.</div>;
  const s = query.data && typeof query.data === 'object' ? query.data : null;
  if (!s && query.isError) return <div className="ai-admin-note tm-error-text" style={{ marginTop: 6 }}>{runnerStatusReadError(query.error)}</div>;
  if (!s) return null;
  const info = runnerStatusInfo(s.status);
  const banner = modeBanner(isDryRun(s));
  const lastError = runnerErrorText(s.lastError);
  return (
    <>
      <div className="ai-admin-row" style={{ marginBottom: 0 }}>
        <span>Runner:</span>
        <span className={`tm-chip ${CHIP_CLASS[info.kind] || ''}`} title={info.text}>{info.label}</span>
        <span className={`tm-chip ${banner.live ? 'tm-chip-bad' : 'tm-chip-info'}`} title={banner.title}>{banner.text}</span>
        <span className="ai-admin-note">{info.text}</span>
      </div>
      {lastError && <div className="ai-admin-note tm-error-text">Runner's last error: {text(lastError)}</div>}
    </>
  );
}

// One saved Auto login: its session, calls in the last 10 s, its 429 / pace
// chip (runnerLogin: the runner's status.sessions entry), why the runner set
// it aside, its last error, and its own paste / renew panel
function AutoLoginRow({ id, session, runnerLogin, panelSession, credentialsEnabled, isOpen, onToggle, onMessage }) {
  const s = session || {};
  const isLive = s.status === 'ok';
  const expires = parseUtcDate(s.expiresAt);
  const name = sessionName(session, id);
  const chip = loginPaceChip({ last429At: s.last429At, pace: runnerLogin?.pace ?? null });
  const setAside = isLive && runnerLogin?.authPaused && typeof runnerLogin.authPaused === 'object' ? runnerLogin.authPaused : null;
  return (
    <div className="ds-slot-row">
      <div className="ds-login-current">
        <b>{name}</b>
        <SkyboxSessionChip session={session} />
        <span className="ds-num">{fmtInt(s.callsLast10s ?? 0)} calls in the last 10 s</span>
        <span className={`tm-chip ${CHIP_CLASS[chip.kind] || ''}`} title="Skybox's last 429 on this login (since the server restarted), and the runner's pacing on it: fast = no 429 in 5 min">
          {chip.text}
        </span>
        {isLive && expires && <span className="ai-admin-note">renews itself · token expires {formatTimeEastern(expires)}</span>}
      </div>
      {!isLive && (
        <div className="tm-warn" style={{ marginTop: 4 }}>
          Not live — Lion Auto skips {name} until its Skybox session works again: paste fresh tokens below (or in Admin → Users).
        </div>
      )}
      {setAside && (
        <div className="tm-warn" style={{ marginTop: 4 }}>
          Lion Auto set it aside: {text(setAside.message)}. Checked again by {formatTimeEastern(setAside.nextProbeAt)}, or at once when its session is renewed.
        </div>
      )}
      {text(s.lastError) && <div className="ai-admin-note tm-error-text">Last error: {text(s.lastError)}</div>}
      <div className="ds-auto-session">
        <button className="btn-secondary btn-sm" onClick={onToggle} aria-label={`Paste / renew ${name}'s Skybox session`} aria-expanded={isOpen}>
          {isOpen ? 'Hide its Skybox session' : 'Paste / renew its Skybox session'}
        </button>
        {isOpen && (
          <SkyboxSessionPanel
            userId={id}
            username={text(s.username) || `user-${id}`}
            session={panelSession || session}
            credentialsEnabled={credentialsEnabled}
            onMessage={onMessage}
          />
        )}
      </div>
    </div>
  );
}

// Every user with a Skybox session, ticked when picked; a blocked one can't be
// ticked (an already-saved one can still be unticked), nor any more at the cap
function AutoLoginChecklist({ sessions, selected, currentIds, isDisabled, onToggle }) {
  if (!sessions.length) return <div className="ai-admin-note" style={{ marginTop: 6 }}>No user has a Skybox session yet.</div>;
  const isFull = selected.length >= MAX_AUTO_LOGINS;
  return (
    <div className="ds-login-list" role="group" aria-label="Lion Auto's Skybox logins">
      {sessions.map(s => {
        const isChecked = selected.includes(s.userId);
        const why = blockedWhy(s) || (isFull && !isChecked ? `at most ${MAX_AUTO_LOGINS} Auto logins` : null);
        return (
          <label key={s.userId} className={why ? 'ds-login-blocked' : ''} title={why || undefined}>
            <input type="checkbox" checked={isChecked} onChange={(e) => onToggle(s.userId, e.target.checked)} disabled={isDisabled || (!!why && !isChecked)} />
            <span>{sessionName(s)}</span>
            {s.username && s.displayName && s.username !== s.displayName && <span className="ai-admin-note">{text(s.username)}</span>}
            <SkyboxSessionChip session={s} />
            {s.userActive === true && <span className="tm-chip tm-chip-warn">active user — not allowed</span>}
            {s.userActive === false && <span className="tm-chip">inactive user</span>}
            {s.purpose === 'backfill' && <span className="tm-chip">backfill login</span>}
            {currentIds.includes(s.userId) && <span className="tm-chip tm-chip-info">Lion Auto login</span>}
          </label>
        );
      })}
    </div>
  );
}

function AutoLoginSteps() {
  return (
    <>
      <div className="ai-admin-note" style={{ marginTop: 8 }}>Add an Auto login (each has its own Skybox budget, so more logins = more room):</div>
      <ol className="ds-steps">
        <li>Admin → Users: create an <b>inactive</b> user per login (role pricer): <code>lion-auto</code>, <code>lion-auto-2</code>, …</li>
        <li>Paste its Skybox session: the token pair of a Skybox login used for nothing else (not a pricer's, not a backfill login).</li>
        <li>Tick it here → <b>Save</b>. The first time, also turn the runner on in Lion Auto's Auto runner panel (it starts in dry run).</li>
      </ol>
    </>
  );
}

// A row per saved Auto login (one paste / renew panel open at a time), then
// the runner's line
function SavedAutoLogins({ currentIds, sessions, onMessage }) {
  const qc = useQueryClient();
  const { data: skyboxData } = useSkyboxSessions();
  const runnerQ = useLionAutoStatus({ poll: true });
  const [openId, setOpenId] = useState(null); // the login whose paste / renew panel is open
  const runnerLogins = Array.isArray(runnerQ.data?.sessions) ? runnerQ.data.sessions.filter(x => x && typeof x === 'object') : [];
  const panelSessions = Array.isArray(skyboxData?.sessions) ? skyboxData.sessions : [];
  // Pasting tokens in a panel re-reads the sessions; this card reads them from
  // the dataset status and the runner lists the logins, so both follow
  const handleSessionMessage = (msg, isError) => {
    onMessage?.(msg, isError);
    qc.invalidateQueries({ queryKey: datasetKeys.status });
    qc.invalidateQueries({ queryKey: lionAutoKeys.autoStatus });
  };
  return (
    <div className="ds-slot-list">
      {currentIds.length === 0 && <div className="tm-warn">None assigned — Auto does nothing, whatever groups are on Auto.</div>}
      {currentIds.map(id => (
        <AutoLoginRow
          key={id}
          id={id}
          session={sessions.find(s => s.userId === id)}
          runnerLogin={runnerLogins.find(x => x.userId === id)}
          panelSession={panelSessions.find(s => s?.userId === id)}
          credentialsEnabled={skyboxData?.credentialsEnabled !== false}
          isOpen={openId === id}
          onToggle={() => setOpenId(v => (v === id ? null : id))}
          onMessage={handleSessionMessage}
        />
      ))}
      <AutoRunnerLine query={runnerQ} />
    </div>
  );
}

// The card once the status names the Auto logins: the checklist, Save, a row
// per saved login, the runner's line, the setup steps
function AutoLoginsBody({ status, onMessage }) {
  const setAuto = useSetDatasetAutoSession();
  const [picks, setPicks] = useState(null);   // null = untouched (show the saved ones)
  const sessions = statusSessions(status);
  const currentIds = autoIds(status);
  const selected = picks ?? currentIds;
  const isDirty = !sameIdSet(selected, currentIds);
  const pickedBlocked = sessions.filter(s => selected.includes(s.userId) && blockedWhy(s));

  const handleToggle = (id, on) => setPicks(on ? [...selected.filter(x => x !== id), id] : selected.filter(x => x !== id));
  const handleSave = () => {
    if (!isDirty || setAuto.isPending || pickedBlocked.length) return;
    if (!selected.length && !window.confirm("Clear every Lion Auto login? Auto does nothing until one is assigned again.")) return;
    setAuto.mutate(selected, {
      onSuccess: () => {
        setPicks(null);
        onMessage?.(selected.length ? `Lion Auto logins saved: ${selected.length}` : "Lion Auto's logins cleared — Auto does nothing until one is assigned");
      },
      onError: (err) => onMessage?.(err?.response?.status === 503
        ? "This server can't assign Lion Auto's logins yet — deploy the update first"
        : errorMessage(err, "Failed to save Lion Auto's logins"), true),
    });
  };

  return (
    <>
      <AutoLoginChecklist sessions={sessions} selected={selected} currentIds={currentIds} isDisabled={setAuto.isPending} onToggle={handleToggle} />
      <div className="ai-admin-row">
        <button className="btn-primary btn-sm" onClick={handleSave} disabled={!isDirty || setAuto.isPending || pickedBlocked.length > 0}>
          {setAuto.isPending ? 'Saving…' : 'Save'}
        </button>
        {isDirty && !setAuto.isPending && <button className="btn-secondary btn-sm" onClick={() => setPicks(null)}>Undo</button>}
        <span className="ai-admin-note">{fmtInt(selected.length)} picked · at most {MAX_AUTO_LOGINS}</span>
      </div>
      {pickedBlocked.length > 0 && (
        <div className="tm-warn">
          Untick {pickedBlocked.map(s => `${sessionName(s)} (${blockedWhy(s)})`).join(', ')} to save: the server refuses it.
        </div>
      )}
      <SavedAutoLogins currentIds={currentIds} sessions={sessions} onMessage={onMessage} />
      <AutoLoginSteps />
    </>
  );
}

// Lion Auto's Skybox logins, or why they can't be shown yet (the backfill
// engine's status carries them; a server before several logins sends no autoUserIds)
function AutoLoginsCard({ status, unavailable, loadError, onMessage }) {
  const isSupported = Array.isArray(status?.autoUserIds);
  let body;
  if (unavailable) body = <div className="ai-admin-note" style={{ marginTop: 6 }}>Backfill engine not running on this server — this card reads its logins from the same status, so nothing to pick yet.</div>;
  else if (loadError) body = <div className="ai-admin-note tm-error-text">{loadError}</div>;
  else if (!status) body = <div className="ai-admin-note">Loading…</div>;
  else if (!isSupported) body = <div className="ai-admin-note" style={{ marginTop: 6 }}>This server can't take several Lion Auto logins yet — deploy the update first.</div>;
  else body = <AutoLoginsBody status={status} onMessage={onMessage} />;
  return (
    <div className="ai-admin-card ds-login-card">
      <h4>Auto logins (Lion Auto)</h4>
      <div className="ai-admin-note">
        Lion Auto writes prices to Skybox with these logins when a group is set to Auto: one login per run, a login without a recent 429 first. Each must be a dedicated login, not a pricer's. Without one, Auto does nothing.
      </div>
      {body}
    </div>
  );
}

export default AutoLoginsCard;
